import React, { useState } from 'react';
import { Monitor, Mail, Lock, Globe, AlertCircle, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Alert, AlertDescription } from './ui/alert';
import beszelApi from '../services/beszelApi';

const SimpleDashboard = () => {
  const [config, setConfig] = useState({
    baseUrl: '',
    email: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [isConnecting, setIsConnecting] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [systems, setSystems] = useState([]);

  const handleConfigSubmit = async (e) => {
    e.preventDefault();
    if (!config.baseUrl || !config.email || !config.password) {
      setError('请填写完整的配置信息');
      return;
    }
    
    setIsConnecting(true);
    setError('');
    
    try {
      console.log('开始连接Beszel服务器:', config.baseUrl);
      const result = await beszelApi.initialize(
        config.baseUrl,
        config.email,
        config.password
      );
      
      if (result.success) {
        console.log('连接成功，开始加载系统数据');
        setIsConnected(true);
        
        // 尝试加载系统数据
        try {
          const systemsData = await beszelApi.getSystemsWithLatestStats();
          console.log('系统数据加载完成:', systemsData);
          setSystems(systemsData);
        } catch (systemError) {
          console.error('加载系统数据失败:', systemError);
          setError('连接成功但加载数据失败: ' + systemError.message);
        }
      } else {
        console.error('连接失败:', result.error);
        setError(result.error || '连接失败');
      }
    } catch (error) {
      console.error('连接异常:', error);
      setError('连接失败: ' + error.message);
    } finally {
      setIsConnecting(false);
    }
  };

  const handleDisconnect = () => {
    beszelApi.disconnect();
    setIsConnected(false);
    setSystems([]);
    setConfig({ baseUrl: '', email: '', password: '' });
    setError('');
  };

  if (isConnected) {
    return (
      <div className="container mx-auto px-4 py-8 space-y-6">
        <Card>
          <CardHeader>
            <div className="flex justify-between items-center">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Monitor className="w-6 h-6" />
                  系统监控仪表板
                </CardTitle>
                <CardDescription>
                  连接到: {config.baseUrl}
                </CardDescription>
              </div>
              <Button variant="outline" onClick={handleDisconnect}>
                断开连接
              </Button>
            </div>
          </CardHeader>
        </Card>

        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {systems.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {systems.map((system) => (
              <Card key={system.id} className="hover:shadow-lg transition-shadow">
                <CardHeader className="pb-3">
                  <div className="flex justify-between items-start">
                    <div>
                      <CardTitle className="text-lg">{system.name}</CardTitle>
                      <CardDescription>{system.host}:{system.port}</CardDescription>
                    </div>
                    <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                      system.latestStats?.status === 'up' 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {system.latestStats?.status?.toUpperCase() || 'UNKNOWN'}
                    </div>
                  </div>
                </CardHeader>
                
                <CardContent>
                  {system.latestStats ? (
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-muted-foreground">CPU使用率</span>
                        <span className="text-sm font-medium">{system.latestStats.cpu?.toFixed(1)}%</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-muted-foreground">内存使用率</span>
                        <span className="text-sm font-medium">{system.latestStats.mem?.toFixed(1)}%</span>
                      </div>
                      <div className="text-xs text-muted-foreground pt-2 border-t">
                        最后更新: {new Date(system.latestStats.created).toLocaleString()}
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <div className="text-muted-foreground">暂无数据</div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card>
            <CardContent className="flex items-center justify-center py-12">
              <div className="text-center">
                <Monitor className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <div className="text-muted-foreground">暂无系统数据</div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md space-y-8">
        {/* Logo/Brand Area */}
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-primary rounded-full mb-4">
            <Monitor className="w-8 h-8 text-primary-foreground" />
          </div>
          <h1 className="text-3xl font-bold tracking-tight">Beszel 监控系统</h1>
          <p className="text-muted-foreground mt-2">连接您的监控仪表板</p>
        </div>

        {/* Login Form */}
        <Card>
          <CardHeader>
            <CardTitle>登录到监控系统</CardTitle>
            <CardDescription>
              输入您的 Beszel 服务器信息以连接到监控仪表板
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleConfigSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="server-url" className="flex items-center gap-2">
                  <Globe className="w-4 h-4" />
                  服务器地址
                </Label>
                <Input
                  id="server-url"
                  type="url"
                  placeholder="https://your-beszel-server.com"
                  value={config.baseUrl}
                  onChange={(e) => setConfig({...config, baseUrl: e.target.value})}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email" className="flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  邮箱地址
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="your@email.com"
                  value={config.email}
                  onChange={(e) => setConfig({...config, email: e.target.value})}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="flex items-center gap-2">
                  <Lock className="w-4 h-4" />
                  密码
                </Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="请输入密码"
                  value={config.password}
                  onChange={(e) => setConfig({...config, password: e.target.value})}
                  required
                />
              </div>

              {error && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <Button 
                type="submit" 
                className="w-full" 
                disabled={isConnecting}
              >
                {isConnecting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    连接中...
                  </>
                ) : (
                  <>
                    <Monitor className="mr-2 h-4 w-4" />
                    连接 Beszel
                  </>
                )}
              </Button>
            </form>

            {/* Help Section */}
            <div className="mt-6 p-4 bg-muted rounded-lg">
              <h3 className="text-sm font-medium mb-2 flex items-center gap-2">
                <AlertCircle className="w-4 h-4" />
                使用说明
              </h3>
              <ul className="text-xs text-muted-foreground space-y-1">
                <li>• 确保您的 Beszel 服务器已正确配置并运行</li>
                <li>• 输入完整的服务器地址，包括协议 (https://)</li>
                <li>• 使用注册的邮箱和密码登录</li>
                <li>• 确保服务器地址可以正常访问</li>
              </ul>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SimpleDashboard;